import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, ArrowLeft, ArrowUpRight } from 'lucide-react';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FinalCTA } from '../components/Sections';
import { BLOG_POSTS } from '../mock';

const Block = ({ b }) => {
  if (b.type === 'h2') {
    return <h2 className="mt-10 mb-3 text-2xl md:text-3xl font-extrabold text-[#0a0e27]">{b.text}</h2>;
  }
  if (b.type === 'ul') {
    return (
      <ul className="my-5 space-y-2.5">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-700 leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF5B24] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.type === 'quote') {
    return (
      <blockquote className="my-8 border-l-4 border-[#FF5B24] bg-[#FFF1EA] rounded-r-2xl px-6 py-5 text-lg font-semibold text-[#0a0e27] italic">
        {b.text}
      </blockquote>
    );
  }
  return <p className="my-4 text-[17px] text-slate-700 leading-relaxed">{b.text}</p>;
};

const RelatedCard = ({ p }) => (
  <Link to={`/blog/${p.slug}`} className="group block rounded-2xl overflow-hidden border border-slate-100 bg-white hover:shadow-xl transition-all">
    <div className={`relative bg-gradient-to-br ${p.gradient} aspect-[16/9] overflow-hidden`}>
      <div className="absolute inset-0 bg-black/10" />
      <div className="absolute inset-0 bg-grid opacity-10" />
      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[#0a0e27] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
        {p.category}
      </span>
    </div>
    <div className="p-5">
      <h3 className="font-extrabold text-[#0a0e27] group-hover:text-[#FF5B24] transition-colors leading-tight">{p.title}</h3>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
        <Clock size={12} /> {p.readTime}
      </div>
    </div>
  </Link>
);

const NotFound = () => (
  <>
    <Seo title="Artigo não encontrado" description="O artigo que você procura não está disponível." path="/blog" />
    <Header />
    <section className="min-h-[60vh] flex items-center justify-center bg-white px-4">
      <div className="text-center">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">Blog CoreDesk</div>
        <h1 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a0e27]">Artigo não encontrado</h1>
        <p className="mt-3 text-slate-600">O conteúdo que você procura pode ter sido movido ou removido.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 btn-coral rounded-full h-11 px-6 text-sm font-semibold">
          <ArrowLeft size={16} /> Voltar para o blog
        </Link>
      </div>
    </section>
    <Footer />
  </>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) return <NotFound />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <Seo title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} />
      <Header />

      {/* Hero do artigo */}
      <section className={`relative bg-gradient-to-br ${post.gradient} overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-semibold">
            <ArrowLeft size={16} /> Voltar para o blog
          </Link>
          <span className="mt-6 inline-block bg-white/90 backdrop-blur text-[#0a0e27] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            {post.category}
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-extrabold text-white leading-[1.1]">{post.title}</h1>
          <div className="mt-6 flex items-center gap-4 text-sm text-white/90">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">
                {post.author.charAt(0)}
              </div>
              <span className="font-semibold">{post.author}</span>
            </div>
            <span className="opacity-60">•</span>
            <span>{post.date}</span>
            <span className="opacity-60">•</span>
            <span className="flex items-center gap-1.5"><Clock size={13} /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Corpo do artigo */}
      <article className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-slate-600 leading-relaxed font-medium border-b border-slate-100 pb-8">
            {post.excerpt}
          </p>
          {post.content.map((b, i) => <Block key={i} b={b} />)}
        </div>
      </article>

      {/* Relacionados */}
      <section className="py-16 bg-[#FAF6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a0e27]">Continue lendo</h2>
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF5B24] hover:underline">
              Ver todos <ArrowUpRight size={15} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => <RelatedCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </>
  );
};

export default BlogPost;
